import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useFailedSubmissions, type FailedSubmission } from '@/hooks/use-failed-submissions';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const errorTypeLabels: Record<string, string> = {
  missing_group_id: 'Groep ID ontbreekt',
  group_not_found: 'Groep niet gevonden',
  missing_email: 'Email ontbreekt',
  processing_error: 'Verwerkingsfout',
  validation_error: 'Validatiefout',
};

const statusLabels: Record<string, string> = {
  pending: 'In behandeling',
  resolved: 'Opgelost',
  ignored: 'Genegeerd',
};

export function FailedSubmissionsPage() {
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'pending' | 'resolved' | 'ignored' | 'all'>('pending');
  const {
    data: failedSubmissions,
    isLoading,
    isError,
    error,
  } = useFailedSubmissions(statusFilter === 'all' ? {} : { status: statusFilter });

  const columns: ColumnDef<FailedSubmission>[] = [
    {
      accessorKey: 'createdAt',
      header: 'Datum',
      cell: ({ row }) => (
        <div className="text-sm">
          {new Date(row.original.createdAt).toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      ),
    },
    {
      accessorKey: 'errorType',
      header: 'Type fout',
      cell: ({ row }) => (
        <Badge variant="outline" className="whitespace-nowrap">
          {errorTypeLabels[row.original.errorType] || row.original.errorType}
        </Badge>
      ),
    },
    {
      accessorKey: 'errorMessage',
      header: 'Foutmelding',
      cell: ({ row }) => <div className="max-w-md truncate text-sm">{row.original.errorMessage}</div>,
    },
    {
      accessorKey: 'externalSubmissionId',
      header: 'Submission ID',
      cell: ({ row }) => (
        <code className="text-xs text-muted-foreground">
          {row.original.externalSubmissionId?.slice(0, 12) || 'N/A'}
        </code>
      ),
    },
    {
      accessorKey: 'attemptCount',
      header: 'Pogingen',
      cell: ({ row }) => <div className="text-center">{row.original.attemptCount}</div>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        let icon;
        let variant: 'default' | 'destructive' | 'outline' | 'secondary' = 'secondary';

        if (status === 'resolved') {
          icon = <CheckCircle2 className="mr-1 h-3 w-3" />;
          variant = 'default';
        } else if (status === 'ignored') {
          icon = <XCircle className="mr-1 h-3 w-3" />;
          variant = 'secondary';
        } else {
          icon = <AlertCircle className="mr-1 h-3 w-3" />;
          variant = 'destructive';
        }

        return (
          <Badge variant={variant} className="flex w-fit items-center whitespace-nowrap">
            {icon}
            {statusLabels[status] || status}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Link to={`/mislukte-inzendingen/${row.original.id}`}>
            <Button variant="ghost" size="sm">
              Bekijken
            </Button>
          </Link>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: failedSubmissions || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div role="alert" className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Mislukte Inzendingen</h1>
        <p>Mislukte inzendingen konden niet volledig worden geladen: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mislukte Inzendingen</h1>
          <p className="text-muted-foreground">Beheer webhook inzendingen die niet konden worden verwerkt</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Input
          placeholder="Zoeken..."
          value={globalFilter}
          onChange={e => setGlobalFilter(e.target.value)}
          className="max-w-sm"
        />
        <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter op status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="pending">In behandeling</SelectItem>
            <SelectItem value="resolved">Opgelost</SelectItem>
            <SelectItem value="ignored">Genegeerd</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Geen mislukte inzendingen gevonden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{table.getFilteredRowModel().rows.length} resultaten</div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Vorige
          </Button>
          <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            Volgende
          </Button>
        </div>
      </div>
    </div>
  );
}
